import { StockOrderEditDataItem, SupplierCatalogItem } from '../../models';

export interface CatalogCartItem {
    key: string; // master_product_company_instance_id or product_uuid
    master_product_id: number | null;
    master_product_company_instance_id: number | null;
    name: string;
    reorder_code: string;
    case_qty: number | null;
    existing_product_uuid: string | null;
    /** Number of cases (matches mobile `casesDelivered`). Units = cases × case_qty. */
    quantity: number;
}

export interface CatalogOrderSavedSnapshot {
    description: string;
    cartItems: CatalogCartItem[];
}

export const DEFAULT_CATALOG_ROW_QUANTITY = 1;

/** Coerce a raw input to a positive integer quantity; invalid/empty falls back to default. */
export const normalizeCatalogQuantity = (
    value: unknown,
    fallback: number = DEFAULT_CATALOG_ROW_QUANTITY,
): number => {
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
        return fallback;
    }
    return Math.floor(parsed);
};

/** Units for a cart line (cases × pack size), matching mobile `unitsDelivered`. */
export const getCartItemUnits = (item: CatalogCartItem): number => {
    const cases = Number(item.quantity) || 0;
    const packSize = item.case_qty != null && Number(item.case_qty) > 0
        ? Number(item.case_qty)
        : 1;
    return cases * packSize;
};

/** Convert stored unit quantity into cases for edit preload. */
export const unitsToCases = (
    units: number,
    caseQty: number | null | undefined,
): number => {
    const packSize = caseQty != null && Number(caseQty) > 0 ? Number(caseQty) : 1;
    if (!Number.isFinite(units) || units <= 0) {
        return DEFAULT_CATALOG_ROW_QUANTITY;
    }
    return Math.max(1, Math.round(units / packSize));
};

/** Stable cart/row key for a catalog browse item (MCPI or local product). */
export const getCatalogItemKey = (item: Pick<
    SupplierCatalogItem,
    'master_product_company_instance_id' | 'existing_product_uuid' | 'id'
>): string => {
    if (item.master_product_company_instance_id != null) {
        return String(item.master_product_company_instance_id);
    }
    if (item.existing_product_uuid) {
        return `product:${item.existing_product_uuid}`;
    }
    return `catalog:${item.id}`;
};

export const catalogItemToCartItem = (
    item: SupplierCatalogItem,
    cases: number = DEFAULT_CATALOG_ROW_QUANTITY,
): CatalogCartItem => ({
    key: getCatalogItemKey(item),
    master_product_id: item.master_product_id,
    master_product_company_instance_id: item.master_product_company_instance_id,
    name: item.name,
    reorder_code: item.reorder_code,
    case_qty: item.case_qty,
    existing_product_uuid: item.existing_product_uuid,
    quantity: normalizeCatalogQuantity(cases),
});

export const editDataItemToCartItem = (item: StockOrderEditDataItem): CatalogCartItem => {
    const mcpiId = item.master_product_company_instance_id;
    const productUuid = item.product_uuid;
    const key = mcpiId != null
        ? String(mcpiId)
        : (productUuid ? `product:${productUuid}` : `line:${item.name}:${item.reorder_code}`);
    const caseQty = item.case_qty != null
        ? item.case_qty
        : (item.containerQuantity != null ? Number(item.containerQuantity) : null);
    const storedUnits = item.quantity != null && item.quantity > 0 ? Number(item.quantity) : 1;
    return {
        key,
        master_product_id: item.master_product_id,
        master_product_company_instance_id: mcpiId,
        name: item.name || '',
        reorder_code: item.reorder_code || item.supplierItemNumber || '',
        case_qty: caseQty,
        existing_product_uuid: productUuid,
        quantity: unitsToCases(storedUnits, caseQty),
    };
};

/** Insert a new cart line or increment an existing one by `casesToAdd` cases. */
export const addOrIncrementCatalogCartItem = (
    cartItems: CatalogCartItem[],
    catalogItem: SupplierCatalogItem,
    casesToAdd: number = DEFAULT_CATALOG_ROW_QUANTITY,
): CatalogCartItem[] => {
    const amount = normalizeCatalogQuantity(casesToAdd);
    const key = getCatalogItemKey(catalogItem);
    const existingIndex = cartItems.findIndex((c) => c.key === key);
    if (existingIndex >= 0) {
        const next = [...cartItems];
        next[existingIndex] = {
            ...next[existingIndex],
            quantity: next[existingIndex].quantity + amount,
        };
        return next;
    }
    return [...cartItems, catalogItemToCartItem(catalogItem, amount)];
};

/** Client-side filter of cart/items lines by product name or reorder code. */
export const filterCatalogCartItems = (
    items: CatalogCartItem[],
    searchTerm: string,
): CatalogCartItem[] => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) {
        return items;
    }
    return items.filter((item) => {
        const name = (item.name || '').toLowerCase();
        const reorderCode = (item.reorder_code || '').toLowerCase();
        return name.includes(needle) || reorderCode.includes(needle);
    });
};

/** Sum of case quantities for Items stats. */
export const sumCartQuantities = (items: CatalogCartItem[]): number => (
    items.reduce((total, item) => total + (Number(item.quantity) || 0), 0)
);

/** Sum of unit quantities (cases × case_qty) for Items stats. */
export const sumCartUnits = (items: CatalogCartItem[]): number => (
    items.reduce((total, item) => total + getCartItemUnits(item), 0)
);

export const formatCatalogNullableNumber = (value: number | null | undefined): string => {
    if (value == null || !Number.isFinite(Number(value))) {
        return '—';
    }
    return String(value);
};

export const formatCatalogCurrency = (value: number | null | undefined): string => {
    if (value == null || !Number.isFinite(Number(value))) {
        return '—';
    }
    return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

export const formatCatalogDate = (value: string | null | undefined): string => {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '—';
    }
    return date.toLocaleDateString();
};

export const buildCatalogOrderSnapshot = (
    description: string,
    cartItems: CatalogCartItem[],
): CatalogOrderSavedSnapshot => ({
    description: description || '',
    cartItems: cartItems.map((item) => ({ ...item })),
});

const normalizeCartForCompare = (items: CatalogCartItem[]) => (
    [...items]
        .map((item) => ({
            key: item.key,
            quantity: Number(item.quantity) || 0,
            case_qty: item.case_qty != null ? Number(item.case_qty) : null,
            reorder_code: item.reorder_code || '',
            name: item.name || '',
            master_product_id: item.master_product_id,
            master_product_company_instance_id: item.master_product_company_instance_id,
            existing_product_uuid: item.existing_product_uuid,
        }))
        .sort((a, b) => a.key.localeCompare(b.key))
);

export const catalogOrderHasUnsavedChanges = (
    saved: CatalogOrderSavedSnapshot | null,
    description: string,
    cartItems: CatalogCartItem[],
): boolean => {
    if (!saved) {
        return cartItems.length > 0 || Boolean((description || '').trim());
    }
    if ((saved.description || '') !== (description || '')) {
        return true;
    }
    return JSON.stringify(normalizeCartForCompare(saved.cartItems))
        !== JSON.stringify(normalizeCartForCompare(cartItems));
};

// Universal Catalog Ordering (FR-01/FR-02): builds the stock order payload
// for cart lines from the master catalog browse flow and for edit preloads.
// Catalog-only lines omit uuid so the async task resolves/creates products.
// Product-backed lines (edit mode, or local ProductSupplier extras with no MCPI)
// include uuid/article so existing products are reused.
// Quantity fields sent to the API are **units** (cases × case_qty), matching mobile.
export const getCatalogStockOrderRequestData = (
    selectedSupplier: any,
    selectedLocation: any,
    description: string,
    cartItems: CatalogCartItem[],
    options?: { includeProductUuids?: boolean },
) => {
    // Create: omit product uuids for master-catalog lines so they route to the
    // async task. Always include uuids for product-only lines (no MCPI).
    // Edit: include uuids so existing lines reuse local Products.
    const includeProductUuids = Boolean(options && options.includeProductUuids);
    const items = cartItems.map((item, index) => {
        const units = getCartItemUnits(item);
        const payload: Record<string, unknown> = {
            itemIndex: index + 1,
            name: item.name,
            supplierItemNumber: item.reorder_code || null,
            actualGoods: units,
            quantity: units,
            desiredOverallQuantity: units,
            containerQuantity: item.case_qty ? Number(item.case_qty) : 0,
            reorder_code: item.reorder_code || null,
            case_qty: item.case_qty || null,
        };
        if (item.master_product_id != null) {
            payload.master_product_id = item.master_product_id;
        }
        if (item.master_product_company_instance_id != null) {
            payload.master_product_company_instance_id = item.master_product_company_instance_id;
        }
        const isProductOnlyLine = (
            item.existing_product_uuid
            && item.master_product_company_instance_id == null
        );
        if ((includeProductUuids || isProductOnlyLine) && item.existing_product_uuid) {
            payload.uuid = item.existing_product_uuid;
            payload.article = item.existing_product_uuid;
        }
        return payload;
    });

    return {
        supplier: (
            (selectedSupplier && selectedSupplier.uuid)
            || (selectedSupplier && selectedSupplier.korona_supplier && selectedSupplier.korona_supplier.uuid)
        ),
        target: selectedLocation.korona_org_unit,
        description,
        location: selectedLocation.id,
        items,
    };
};
